/**
 * Menu MenuButton  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { Type, defineElementaryComponent, createSubScope } from "@adapptio/lib-blueprint";

import {
	ARROW_KEYS_NAVIGATION_TYPE,
	Button,
	ClassList,
	getStringEnumKeyByValue,
	Icon,
	ICON_NAME,
	ICON_SIZE_CLASS,
	IPopupRefProps,
	Popup,
	TViewComponentDefinition,
	TComponentReact,
	useArrowKeysNavigation
} from "@adapptio/lib-components";

import { isNonEmptyArray, isNonEmptyObject } from "@adapptio/lib-shared";

import { termsEditor } from "../../terms";
import { ComponentMenuButton_Props } from "./props";
import { resolveButtonStyle } from "../Button/resolveButtonStyle";
import {
	HORIZONTAL_ALIGN_TO_POPUP_POSITION,
	POPUP_POSITION
} from "@adapptio/lib-components/src/Enums/POPUP_POSITION";

interface ComponentMenuButton_State {}

const ComponentMenuButton_Events = {
	itemClick: {
		...termsEditor.schemas.menuButton.events.itemClick
	}
};

const ComponentMenuButton_Definition = defineElementaryComponent<
	typeof ComponentMenuButton_Props,
	ComponentMenuButton_State,
	typeof ComponentMenuButton_Events
>({
	...termsEditor.components.menuButton.component,

	name: "menuButton",

	category: "content",

	icon: "mdi/arrow-down-drop-circle",

	events: ComponentMenuButton_Events,

	docUrl: "...",

	order: 40,

	props: ComponentMenuButton_Props,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentMenuButton_React: TComponentReact<typeof ComponentMenuButton_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList,
	additionalProps
}) => {
	const items = Array.isArray(props.items) ? props.items.filter((item) => isNonEmptyObject(item)) : [];

	const { safePath: componentPath, componentMode } = componentInstance;

	const [ active, setActive ] = React.useState(false);

	const elementRef = React.useRef<HTMLDivElement>();
	const popupRef = React.useRef<IPopupRefProps>();

	const buttonProps = {
		...(additionalProps?.resolvedButtonStyle || resolveButtonStyle(props.styleData))
	};

	const popupPosition = React.useMemo(() => {
		return getStringEnumKeyByValue(
			POPUP_POSITION,
			HORIZONTAL_ALIGN_TO_POPUP_POSITION.BOTTOM[props.menuHorizontalAlign]
		);
	}, [ props.menuHorizontalAlign ]);

	const { classList } = ClassList.getElementClassListAndIdClassName(
		"cmp-menu-button",
		componentInstance.safePath,
		{ componentClassList: reactComponentClassList, componentMode }
	);

	const _buttonClickHandler = React.useCallback(() => {
		if (!popupRef.current) {
			return;
		}

		setActive(popupRef.current.toggle(elementRef.current));
	}, []);

	const _popupKeyDownHandler = useArrowKeysNavigation(
		ARROW_KEYS_NAVIGATION_TYPE.VERTICAL,
		".cmp-menu-button__popup"
	);

	const _popupHideHandler = React.useCallback(() => {
		setActive(false);
	}, []);

	return (
		<div ref={elementRef} className={classList.toClassName()}>
			<Button
				{...buttonProps}
				labelText={props.labelText}
				labelIcon={props.labelIcon}
				enabled={props.enabled}
				active={active}
				componentPath={[ ...componentPath, "button" ]}
				componentMode={componentMode}
				classList={new ClassList("button--layout", "cmp-menu-button__button")}
				stopPropagationOnClick={true}
				onClick={_buttonClickHandler}
			>
				<Icon
					source={ICON_NAME.MENU_DOWN}
					size={getStringEnumKeyByValue(ICON_SIZE_CLASS, ICON_SIZE_CLASS.MEDIUM)}
					componentPath={[ ...componentPath, "button-icon" ]}
					componentMode={componentMode}
					classList={new ClassList("cmp-menu-button__button-icon")}
				/>
			</Button>

			{isNonEmptyArray(items) ? (
				<Popup
					ref={popupRef}
					position={popupPosition}
					focus={true}
					componentPath={[ ...componentPath, "popup" ]}
					componentMode={componentMode}
					classList={new ClassList("cmp-menu-button__popup")}
					onKeyDownCapture={_popupKeyDownHandler}
					onHide={_popupHideHandler}
				>
					<ul className="cmp-menu-button__items">
						{items.map((item, index) => {
							if (!item.visible) {
								return null;
							}

							const itemKey = item.key ? `id-${item.key}` : `${index}`;

							const itemClassList = ClassList.fromObject({
								"cmp-menu-button__item": true,
								"cmp-menu-button__item--separated": item.separated
							});

							function _itemClickHandler() {
								if (item.hideOnClick && popupRef.current) {
									popupRef.current.hide();
								}

								if (componentInstance.eventEnabled.itemClick) {
									return componentInstance.eventTriggers.itemClick((parentScope) => {
										return createSubScope(parentScope, { item }, { item: Type.Any({}) });
									});
								}

								return;
							}

							return (
								<li key={itemKey} className={itemClassList.toClassName()}>
									<Button
										style="CLEAR"
										labelText={item.labelText}
										labelIcon={item.labelIcon}
										link={item.link}
										tabIndex={0}
										componentPath={[ ...componentPath, itemKey, "button" ]}
										componentMode={componentMode}
										classList={new ClassList("cmp-menu-button__item-button")}
										onClick={_itemClickHandler}
									/>
								</li>
							);
						})}
					</ul>
				</Popup>
			) : null}
		</div>
	);
};

export const ComponentMenuButton: TViewComponentDefinition<typeof ComponentMenuButton_Definition> = {
	...ComponentMenuButton_Definition,
	reactComponent: ComponentMenuButton_React
};
